import React, { useState, useEffect } from 'react';
import { productFeatures } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, Check } from 'lucide-react';
import Lottie from 'lottie-react';

const ProductFeatures = () => {
  const [activeTab, setActiveTab] = useState('influencer-marketing');
  const [lottieData, setLottieData] = useState(null);
  const [talentLottie, setTalentLottie] = useState(null);
  const [prLottie, setPrLottie] = useState(null);
  const [performanceLottie, setPerformanceLottie] = useState(null);

  useEffect(() => {
    fetch('/images/1.json')
      .then(response => response.json())
      .then(data => setLottieData(data))
      .catch(error => console.error('Error loading Influencer Lottie animation:', error));
    
    fetch('/images/2.json')
      .then(response => response.json())
      .then(data => setTalentLottie(data))
      .catch(error => console.error('Error loading Talent Lottie animation:', error));
    
    fetch('/images/3.json')
      .then(response => response.json())
      .then(data => setPrLottie(data))
      .catch(error => console.error('Error loading PR Lottie animation:', error));
    
    fetch('/images/4.json')
      .then(response => response.json())
      .then(data => setPerformanceLottie(data))
      .catch(error => console.error('Error loading Performance Lottie animation:', error));
  }, []);

  const tabs = productFeatures.map(feature => ({
    id: feature.id,
    label: feature.title,
    icon: feature.icon
  }));

  const activeFeature = productFeatures.find(f => f.id === activeTab);

  return (
    <section id="services" className="bg-white py-20 px-6">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm text-gray-500 uppercase tracking-wider mb-4 font-inter font-medium">Our Services</p>
          <h2 className="text-4xl md:text-5xl font-black leading-tight font-mosans">
            EVERYTHING YOU NEED
            <br />
            <span className="block mt-2">To grow your brand</span>
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-3 mb-16 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 rounded-full font-bold transition-all text-sm md:text-base ${
                activeTab === tab.id
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active Feature Content */}
        {activeFeature && (
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wider mb-3 font-inter font-medium">
                {activeFeature.tagline}
              </p>
              <h3 className="text-3xl md:text-4xl font-black mb-6 leading-tight font-mosans">
                {activeFeature.title}
              </h3>
              <p className="text-lg text-gray-600 mb-8 font-inter leading-relaxed">
                {activeFeature.description}
              </p>
              
              {/* Points List */}
              <div className="space-y-4 mb-8">
                {activeFeature.points.map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-gray-700 font-inter">{point}</p>
                  </div>
                ))}
              </div>

              <Button
                variant="default"
                className="bg-black hover:bg-gray-800 text-white rounded-full px-8 py-6 text-base font-bold font-inter group"
              >
                Learn more
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative flex items-center justify-center">
              {activeTab === 'influencer-marketing' && lottieData ? (
                <div className="w-full">
                  <Lottie 
                    animationData={lottieData}
                    loop={true}
                    className="rounded-2xl w-full"
                  />
                </div>
              ) : activeTab === 'talent-management' && talentLottie ? (
                <div className="w-full">
                  <Lottie 
                    animationData={talentLottie}
                    loop={true}
                    className="rounded-2xl w-full"
                  />
                </div>
              ) : activeTab === 'public-relations' && prLottie ? (
                <div className="w-full">
                  <Lottie 
                    animationData={prLottie}
                    loop={true}
                    className="rounded-2xl w-full"
                  />
                </div>
              ) : activeTab === 'performance-marketing' && performanceLottie ? (
                <div className="w-full">
                  <Lottie 
                    animationData={performanceLottie}
                    loop={true}
                    className="rounded-2xl w-full"
                  />
                </div>
              ) : (
                <img
                  src={activeFeature.image}
                  alt={activeFeature.title}
                  className="rounded-2xl shadow-2xl w-full"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductFeatures;
